import BoltIcon from '@mui/icons-material/Bolt'
import BusinessIcon from '@mui/icons-material/Business'
import CampaignIcon from '@mui/icons-material/Campaign'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import GavelIcon from '@mui/icons-material/Gavel'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import PaymentsIcon from '@mui/icons-material/Payments'
import StorageIcon from '@mui/icons-material/Storage'
import TuneIcon from '@mui/icons-material/Tune'
import { featureOrder } from '@/features/dashboard/site-admin/orgFeatureFlags'
import { getRouteEntries, getRouteHref, routeConfig } from '@/routeConfig'

/**
 * The feature key a route's sidebar position is looked up under.
 *
 * Normally the route's own feature flag; `nav.featureKey` lets an always-on
 * route (Power Overview) take a registry position without being flag-gated.
 */
function navFeatureKey(route) {
    return route.requiresFeatureFlag ?? route.nav?.featureKey
}

/**
 * Order a section's routes by the feature registry so the sidebar and the
 * homepage tiles run in the same sequence (see orgFeatureFlags.js `order`).
 *
 * Routes with no registry entry sort last but keep their relative order —
 * Array#sort is stable — so an unregistered route degrades to the bottom of the
 * section rather than jumping somewhere arbitrary.
 */
function orderByFeatureRegistry(routes) {
    return [...routes].sort(
        (a, b) =>
            featureOrder(navFeatureKey(a)) - featureOrder(navFeatureKey(b))
    )
}

function buildNavEntry(route, overrides = {}) {
    return {
        id: route.id,
        label: route.nav?.label ?? route.title,
        termKey: route.termKey ?? route.nav?.termKey,
        path: getRouteHref(route),
        icon: route.nav?.icon ?? route.bottomTab?.icon,
        authRequired:
            route.nav?.authRequired ?? route.bottomTab?.authRequired ?? false,
        staffRequired:
            route.nav?.staffRequired ?? route.bottomTab?.staffRequired ?? false,
        roles:
            route.nav?.roles ??
            route.sectionNav?.roles ??
            route.sectionRoles ??
            undefined,
        matchSubpaths: route.nav?.matchSubpaths ?? route.matchSubpaths,
        description: route.nav?.description,
        descriptionTermKey: route.nav?.descriptionTermKey,
        requiresBilling:
            route.requiresBilling ?? route.nav?.requiresBilling ?? false,
        requiresFeatureFlag:
            route.requiresFeatureFlag ??
            route.nav?.requiresFeatureFlag ??
            undefined,
        requiresPlatformFeature:
            route.requiresPlatformFeature ??
            route.nav?.requiresPlatformFeature ??
            undefined,
        requiresFlavour:
            route.requiresFlavour ?? route.nav?.requiresFlavour ?? undefined,
        requiresGroupAdmin:
            route.requiresGroupAdmin ??
            route.nav?.requiresGroupAdmin ??
            undefined,
        locationScopeAware: route.locationScopeAware === true,
        // Optional count beside the entry: a hook returning a number (0 hides it).
        useBadgeCount: route.nav?.useBadgeCount,
        badgeLabel: route.nav?.badgeLabel,
        ...overrides
    }
}

const routeEntries = getRouteEntries(routeConfig)

export const topLevelItems = routeEntries
    .filter((route) => route.nav?.surface === 'topLevel')
    .map((route) => buildNavEntry(route))

export const settingsItems = routeEntries
    .filter((route) => route.nav?.surface === 'settings')
    .map((route) => buildNavEntry(route))

/**
 * The Admin area's categories and the pages in each, in display order (route
 * ids from `nav.surface: 'admin'` routes). The Admin overview renders a card
 * per category and AdminLayout's side nav a heading per category, both from
 * this list, so the two can't drift apart. A category's description says what
 * it is *for*, in the words an admin would use to look for it.
 */
const ADMIN_GROUPS = [
    {
        id: 'organisation',
        title: 'Organisation',
        description:
            'Your organisation’s details, the people who use it, and your subscription.',
        icon: <BusinessIcon />,
        routeIds: [
            'admin-users',
            'admin-pupil-groups',
            'admin-org-edit',
            'admin-billing'
        ]
    },
    {
        id: 'estate',
        title: 'Buildings & meters',
        description:
            'The places you monitor, the meters in them, and when they’re in use.',
        icon: <BoltIcon />,
        routeIds: [
            'admin-property-hub',
            'admin-locations',
            'admin-meters',
            'admin-occupancy',
            'admin-holidays'
        ]
    },
    {
        id: 'data',
        title: 'Data & connections',
        description:
            'How readings reach the platform, and whether they’re arriving.',
        icon: <CloudSyncIcon />,
        routeIds: [
            'admin-external-services',
            'admin-fetch-status',
            'admin-data-quality',
            'admin-reading-import',
            'admin-isams-import-failures'
        ]
    },
    {
        id: 'alerts',
        title: 'Alerts',
        description:
            'Rules that watch your energy use, and who hears when they fire.',
        icon: <NotificationsActiveIcon />,
        routeIds: ['admin-alert-rules', 'admin-alert-notifications']
    },
    {
        id: 'finance',
        title: 'Costs & budgets',
        description: 'What you pay for energy, and what you planned to spend.',
        icon: <PaymentsIcon />,
        routeIds: ['admin-finance', 'admin-budgets']
    },
    {
        id: 'content',
        title: 'Dashboard content',
        description: 'Extra figures and comparisons people see on dashboards.',
        icon: <DashboardCustomizeIcon />,
        routeIds: ['admin-equivalents', 'admin-waste']
    }
]

/**
 * Site Admin's categories — the platform operator's equivalent of
 * ADMIN_GROUPS, for `nav.surface: 'site-admin'` routes.
 */
const SITE_ADMIN_GROUPS = [
    {
        id: 'accounts',
        title: 'Organisations & accounts',
        description:
            'Every customer organisation, the people in them, the groups and partners they belong to, and what they pay.',
        icon: <BusinessIcon />,
        routeIds: [
            'site-admin-orgs',
            'site-admin-users',
            'site-admin-groups',
            'site-admin-partners',
            'site-admin-billing'
        ]
    },
    {
        id: 'meter-data',
        title: 'Meter data',
        description:
            'Whether readings are arriving across the platform, the supplier files, access requests and past data requests behind them, and the tools to re-fetch or repair them.',
        icon: <CloudSyncIcon />,
        routeIds: [
            'site-admin-fetch-status',
            'site-admin-meter-feeds',
            'site-admin-meter-access',
            'site-admin-meter-history',
            'site-admin-data-quality',
            'site-admin-meter-refresh',
            'site-admin-tasks'
        ]
    },
    {
        id: 'reference-data',
        title: 'Reference data',
        description:
            'Shared datasets that every organisation’s figures are calculated from.',
        icon: <StorageIcon />,
        routeIds: ['site-admin-weather', 'site-admin-carbon-factors']
    },
    {
        id: 'content',
        title: 'Content & communication',
        description:
            'Learning material, and messages sent to users across the platform.',
        icon: <CampaignIcon />,
        routeIds: ['site-admin-education', 'site-admin-announcements']
    },
    {
        id: 'platform',
        title: 'Platform settings',
        description:
            'Switches that apply to every organisation at once, such as page feedback and self-serve signup.',
        icon: <TuneIcon />,
        routeIds: ['site-admin-features']
    },
    {
        id: 'oversight',
        title: 'Moderation & audit',
        description:
            'What users have flagged for review, feedback staff have left on pages, and a record of who changed what.',
        icon: <GavelIcon />,
        routeIds: [
            'site-admin-reports',
            'site-admin-feedback',
            'site-admin-audit-log'
        ]
    }
]

/**
 * Resolve a category list against the routes on `surface`, warning in
 * development when the two disagree (a page in no category appears nowhere).
 * Returns the categories with their pages' nav entries, unfiltered by access.
 */
function buildAreaGroups(surface, groups) {
    const entries = new Map(
        routeEntries
            .filter((route) => route.nav?.surface === surface)
            .map((route) => [
                route.id,
                buildNavEntry(route, { activePaths: route.nav.activePaths })
            ])
    )

    if (import.meta.env?.DEV) {
        const grouped = new Set(groups.flatMap((group) => group.routeIds))
        const ungrouped = [...entries.keys()].filter((id) => !grouped.has(id))
        const missing = [...grouped].filter((id) => !entries.has(id))
        if (ungrouped.length > 0 || missing.length > 0) {
            console.warn(
                `[navConfig] '${surface}' categories are out of step with routeConfig. Routes in no category: ${ungrouped.join(', ') || 'none'}; category ids with no route: ${missing.join(', ') || 'none'}`
            )
        }
    }

    return groups
        .map(({ routeIds, ...group }) => ({
            ...group,
            items: routeIds.map((id) => entries.get(id)).filter(Boolean)
        }))
        .filter((group) => group.items.length > 0)
}

export const adminNavGroups = buildAreaGroups('admin', ADMIN_GROUPS)
export const siteAdminNavGroups = buildAreaGroups(
    'site-admin',
    SITE_ADMIN_GROUPS
)

export const navSections = routeConfig
    .filter((section) => section.navSection)
    .map((section) => {
        const sectionRoutes = (section.routes || []).filter(
            (route) => route.nav?.surface === 'section'
        )
        let orderedSectionRoutes = sectionRoutes

        if (section.navSection.orderedByFeatureRegistry) {
            orderedSectionRoutes = orderByFeatureRegistry(sectionRoutes)
        }

        return {
            id: section.navSection.id ?? section.id,
            label: section.navSection.label,
            icon: section.navSection.icon,
            discoverPath: orderedSectionRoutes[0]
                ? getRouteHref(orderedSectionRoutes[0])
                : '/',
            pinToTop: section.navSection.pinToTop,
            authRequired: section.navSection.authRequired ?? false,
            staffRequired: section.navSection.staffRequired ?? false,
            roles: section.navSection.roles ?? section.roles,
            requiresGroupAdmin:
                section.navSection.requiresGroupAdmin ?? undefined,
            locationScopeAware: section.navSection.locationScopeAware === true,
            items: orderedSectionRoutes.map((route) => buildNavEntry(route))
        }
    })
    .filter((section) => section.items.length > 0)

// Keep to four entries including More: MUI's BottomNavigation is designed for
// 3-5 and labels truncate on a 320px phone beyond that. `bottomTab.order`
// sets the left-to-right order (Array#sort is stable for ties).
export const bottomNavItems = [
    ...routeEntries
        .filter((route) => route.bottomTab)
        .sort(
            (a, b) =>
                (a.bottomTab.order ?? Number.MAX_SAFE_INTEGER) -
                (b.bottomTab.order ?? Number.MAX_SAFE_INTEGER)
        )
        .map((route) => ({
            id: route.id,
            label: route.bottomTab.label,
            icon: route.bottomTab.icon,
            path: getRouteHref(route),
            authRequired: route.bottomTab.authRequired ?? false,
            staffRequired: route.bottomTab.staffRequired ?? false,
            locationScopeAware: route.locationScopeAware === true,
            roles:
                route.bottomTab.roles ??
                route.nav?.roles ??
                route.sectionNav?.roles ??
                route.sectionRoles ??
                undefined
        })),
    {
        id: 'more',
        label: 'More',
        icon: <MoreHorizIcon />,
        path: null,
        authRequired: true
    }
]
